import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RecurringSubscription, CreateSubscriptionInput } from '@/types/database/recurring-subscriptions';
import { toast } from 'sonner';
import { notifySubscriptionCreated, notifySubscriptionEdited, notifyExpensesGenerated } from '@/services/telegram/subscriptionNotificationService';

export const useRecurringSubscriptions = () => {
  return useQuery({
    queryKey: ['recurring-subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recurring_subscriptions' as any)
        .select(`
          *,
          category:expense_categories(id, name, code),
          department:departments(id, name, code),
          bank_account:bank_accounts(id, name, bank_name)
        `)
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      return data as unknown as RecurringSubscription[];
    },
  });
};

export const useCreateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSubscriptionInput) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('recurring_subscriptions' as any)
        .insert({ ...input, created_by: userData.user?.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-subscriptions'] });
      toast.success('Subscription created successfully');
      // Fire-and-forget Telegram notification
      notifySubscriptionCreated(data as unknown as RecurringSubscription).catch(console.error);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create subscription: ${error.message}`);
    },
  });
};

export const useUpdateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<RecurringSubscription> & { id: string }) => {
      const { data, error } = await supabase
        .from('recurring_subscriptions' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-subscriptions'] });
      toast.success('Subscription updated successfully');
      notifySubscriptionEdited(data as unknown as RecurringSubscription).catch(console.error);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update subscription: ${error.message}`);
    },
  });
};

export const useDeleteSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('recurring_subscriptions' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-subscriptions'] });
      toast.success('Subscription deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete subscription: ${error.message}`);
    },
  });
};

export const useGenerateRecurringExpenses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('generate-recurring-expenses');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      const generated = data?.generated || [];
      const count = generated.length;
      toast.success(count > 0 ? `Generated ${count} expense(s) from subscriptions` : 'No subscriptions due for generation');
      if (count > 0) {
        notifyExpensesGenerated(generated).catch(console.error);
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to generate expenses: ${error.message}`);
    },
  });
};
